/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useState, useCallback, useMemo } from 'react';
import { generateTailoredResume, refineParagraphs } from '../services/api';
import { ResumeData as ParsedResumeData } from '../utils/resumeParser';

// Define the shape of our resume data
export interface ResumeData {
  name: string;
  title: string;
  email: string;
  phone: string;
  location: string;
  linkedin: string;
  summary: string;
  jobField: string;
  enableAIRefinement: boolean;
  targetWordCount: number;
  jobDescription?: string;
  currentResume?: string;
  generatedResume?: string;
  experience: Array<{
    title: string;
    company: string;
    startDate: string;
    endDate: string;
    location: string;
    achievements: string[];
  }>;
  skills: {
    technical: string;
    professional: string;
  };
  education: Array<{
    degree: string;
    field: string;
    school: string;
    year: string;
  }>;
}

export interface WordBanks {
  actionVerbs: string[];
  skillsKeywords: string[];  // Renamed from 'skills' to match vanilla JS
  achievementPhrases: string[];  // Renamed from 'achievements' to match vanilla JS
  industryTerms: string[];
}

export interface AnalysisResults {
  actionVerbs?: string[];
  skillsKeywords?: string[];
  achievementPhrases?: string[];
  industryTerms?: string[];
}

export interface ProgressLog {
  isVisible: boolean;
  phase: string;
  messages: string[];
}

// Additional state interfaces
export interface AppState {
  resumeGuidelines: string[];
  profilePicture: string | null;  // Base64 encoded image
  analysisResults: AnalysisResults | null;
  progressLog: ProgressLog;
  outputVisible: boolean;
  selectedJobField: string;
  parsedResumeData: ParsedResumeData | null;  // Structured resume data for preview modal
  showResumePreviewModal: boolean;  // Resume Preview Modal visibility
}

// Separate contexts for data and actions to prevent unnecessary re-renders
export const ResumeDataContext = createContext<ResumeData | null>(null);
export const WordBanksContext = createContext<WordBanks | null>(null);
export const AppStateContext = createContext<AppState | null>(null);
export const ResumeActionsContext = createContext<{
  setResumeData: React.Dispatch<React.SetStateAction<ResumeData | null>>;
  generateResume: (onProgress?: (msg: string) => void, onPhase?: (phase: string) => void) => Promise<void>;
  setWordBanks: React.Dispatch<React.SetStateAction<WordBanks>>;
  updateWordBank: (bankType: keyof WordBanks, items: string[]) => void;
  addToWordBank: (bankType: keyof WordBanks, item: string) => void;
  removeFromWordBank: (bankType: keyof WordBanks, index: number) => void;
  setResumeGuidelines: React.Dispatch<React.SetStateAction<string[]>>;
  addGuideline: (guideline: string) => void;
  removeGuideline: (index: number) => void;
  setProfilePicture: (picture: string | null) => void;
  setAnalysisResults: (results: AnalysisResults | null) => void;
  logProgress: (message: string) => void;
  setProgressPhase: (phase: string) => void;
  clearProgressLog: () => void;
  showProgressModal: (phase: string) => void;
  hideProgressModal: () => void;
  setOutput: (output: string) => void;
  setOutputVisible: (visible: boolean) => void;
  setSelectedJobField: (field: string) => void;
  loadJobFieldPreset: (field: string) => void;
  setParsedResumeData: (data: ParsedResumeData | null) => void;
  showResumePreview: () => void;
  hideResumePreview: () => void;
  updateCurrentResume: (resume: string) => void;
  updateJobDescription: (description: string) => void;
} | null>(null);

// Combined context for simpler usage (used in tests and components that need all data)
export interface ResumeContextValue {
  resumeData: ResumeData;
  setResumeData: React.Dispatch<React.SetStateAction<ResumeData | null>>;
  jobDescription: string;
  setJobDescription: (desc: string) => void;
  jobField: string;
  setJobField: (field: string) => void;
  wordBanks: WordBanks;
  setWordBanks: React.Dispatch<React.SetStateAction<WordBanks>>;
  appState: AppState;
}

export const ResumeContext = createContext<ResumeContextValue | null>(null);

// Job field presets (ported from index.html lines 682-761)
export const JOB_FIELD_PRESETS: Record<string, {
  actionVerbs: string[];
  skillsKeywords: string[];
  achievementPhrases: string[];
  industryTerms: string[];
  resumeGuidelines: string[];
}> = {
  technology: {
    actionVerbs: ["Developed", "Implemented", "Optimized", "Architected", "Debugged"],
    skillsKeywords: ["JavaScript", "Python", "React", "Node.js", "AWS"],
    achievementPhrases: ["Increased system performance by", "Reduced load time by", "Improved code efficiency", "Led development team of", "Delivered project ahead of schedule"],
    industryTerms: ["Full-stack", "DevOps", "CI/CD", "Microservices", "API"],
    resumeGuidelines: [
      "Start each bullet point with a strong action verb (developed, implemented, optimized, led, etc.)",
      "Quantify achievements with specific metrics (increased performance by 40%, managed team of 12, reduced costs by $50K)",
      "Use the STAR method: Situation, Task, Action, Result for complex achievements",
      "Tailor technical skills to match job requirements and include proficiency levels",
      "Keep bullet points concise (1-2 lines max) but impactful",
      "Use reverse chronological order with consistent formatting",
      "Include relevant certifications, training, and continuous learning initiatives",
      "Highlight cross-functional collaboration and leadership experience",
      "Focus on business impact and problem-solving capabilities",
      "Proofread for grammar, consistency, and ATS-friendly formatting"
    ]
  },
  healthcare: {
    actionVerbs: ["Administered", "Diagnosed", "Treated", "Monitored", "Assessed"],
    skillsKeywords: ["Patient care", "Medical records", "Clinical assessment", "Pharmacology", "Infection control"],
    achievementPhrases: ["Improved patient satisfaction scores by", "Reduced readmission rates", "Managed caseload of", "Implemented safety protocols", "Trained staff on"],
    industryTerms: ["Evidence-based practice", "Interdisciplinary team", "Quality assurance", "Patient advocacy", "Clinical protocols"],
    resumeGuidelines: [
      "Highlight patient care experience and clinical skills",
      "Include relevant certifications and licenses",
      "Emphasize compliance with healthcare regulations",
      "Quantify patient outcomes and quality improvements",
      "Show commitment to continuing education and professional development"
    ]
  },
  finance: {
    actionVerbs: ["Analyzed", "Forecasted", "Budgeted", "Audited", "Reconciled"],
    skillsKeywords: ["Financial modeling", "Risk analysis", "Excel", "SQL", "Tableau"],
    achievementPhrases: ["Increased revenue by", "Reduced costs by", "Managed portfolio worth", "Improved ROI", "Streamlined processes"],
    industryTerms: ["Due diligence", "Asset management", "Derivatives", "Hedge funds", "Private equity"],
    resumeGuidelines: [
      "Quantify financial impact and achievements with specific numbers",
      "Highlight analytical and technical skills",
      "Include relevant certifications (CFA, CPA, FRM)",
      "Emphasize regulatory knowledge and compliance experience",
      "Show experience with financial software and modeling tools"
    ]
  },
  marketing: {
    actionVerbs: ["Launched", "Promoted", "Strategized", "Targeted", "Engaged"],
    skillsKeywords: ["Digital marketing", "SEO", "SEM", "Social media", "Content marketing"],
    achievementPhrases: ["Increased brand awareness by", "Generated leads worth", "Improved conversion rates", "Grew social media following", "Launched successful campaigns"],
    industryTerms: ["Customer acquisition", "Brand positioning", "Market segmentation", "Lead generation", "Customer journey"],
    resumeGuidelines: [
      "Quantify marketing results with metrics (CTR, conversion rates, ROI)",
      "Highlight experience with digital marketing tools and platforms",
      "Show creativity and strategic thinking in campaigns",
      "Include experience with data analysis and performance optimization",
      "Demonstrate understanding of customer behavior and market trends"
    ]
  },
  education: {
    actionVerbs: ["Taught", "Developed", "Designed", "Facilitated", "Mentored"],
    skillsKeywords: ["Curriculum development", "Learning management systems", "Classroom management", "Educational technology", "Assessment design"],
    achievementPhrases: ["Improved student performance by", "Developed curriculum for", "Trained faculty on", "Increased graduation rates", "Published research on"],
    industryTerms: ["Pedagogy", "Andragogy", "Blended learning", "Online education", "Competency-based education"],
    resumeGuidelines: [
      "Highlight student outcomes and learning achievements",
      "Include relevant teaching certifications and credentials",
      "Emphasize curriculum development and instructional design experience",
      "Show commitment to professional development and continuing education",
      "Quantify impact on student success and institutional goals"
    ]
  },
  engineering: {
    actionVerbs: ["Designed", "Engineered", "Analyzed", "Optimized", "Tested"],
    skillsKeywords: ["CAD software", "Project management", "Quality assurance", "Lean manufacturing", "Six Sigma"],
    achievementPhrases: ["Reduced manufacturing costs by", "Improved product efficiency", "Led engineering team of", "Delivered project under budget", "Increased production capacity"],
    industryTerms: ["Product lifecycle", "Design for manufacturing", "Continuous improvement", "Root cause analysis", "Risk assessment"],
    resumeGuidelines: [
      "Emphasize technical expertise and problem-solving abilities",
      "Include relevant engineering certifications and licenses",
      "Highlight project management and leadership experience",
      "Quantify engineering achievements with specific metrics",
      "Show experience with industry-standard tools and methodologies"
    ]
  },
  consulting: {
    actionVerbs: ["Advised", "Strategized", "Analyzed", "Recommended", "Implemented"],
    skillsKeywords: ["Strategic planning", "Business analysis", "Change management", "Stakeholder management", "Data analysis"],
    achievementPhrases: ["Delivered cost savings of", "Improved operational efficiency", "Led transformation initiative", "Managed client portfolio worth", "Achieved client satisfaction score"],
    industryTerms: ["Management consulting", "Strategy consulting", "Operational excellence", "Digital transformation", "Organizational change"],
    resumeGuidelines: [
      "Highlight client impact and business results achieved",
      "Emphasize analytical and strategic thinking capabilities",
      "Include experience across multiple industries or functional areas",
      "Show progression in project complexity and client responsibility",
      "Demonstrate strong communication and presentation skills"
    ]
  },
  creative: {
    actionVerbs: ["Created", "Designed", "Conceptualized", "Illustrated", "Produced"],
    skillsKeywords: ["Adobe Creative Suite", "UI/UX design", "Brand development", "Visual storytelling", "Typography"],
    achievementPhrases: ["Increased brand engagement by", "Won design award for", "Led creative team of", "Delivered campaign that generated", "Improved user experience"],
    industryTerms: ["Brand identity", "Visual communication", "User experience", "Creative strategy", "Art direction"],
    resumeGuidelines: [
      "Showcase creative portfolio and design achievements",
      "Highlight experience with design tools and technologies",
      "Emphasize collaboration with cross-functional teams",
      "Include awards, recognition, and published work",
      "Show understanding of brand strategy and user needs"
    ]
  },
  legal: {
    actionVerbs: ["Advised", "Negotiated", "Drafted", "Reviewed", "Litigated"],
    skillsKeywords: ["Legal research", "Contract negotiation", "Compliance management", "Risk assessment", "Legal writing"],
    achievementPhrases: ["Successfully negotiated contracts worth", "Reduced legal costs by", "Managed caseload of", "Achieved favorable settlement", "Ensured regulatory compliance"],
    industryTerms: ["Due diligence", "Legal precedent", "Statutory compliance", "Contract law", "Intellectual property"],
    resumeGuidelines: [
      "Highlight bar admissions and legal certifications",
      "Emphasize successful case outcomes and legal achievements",
      "Include experience with relevant legal areas and industries",
      "Show strong analytical and research capabilities",
      "Demonstrate client service and business acumen"
    ]
  },
  nonprofit: {
    actionVerbs: ["Advocated", "Organized", "Fundraised", "Volunteered", "Mobilized"],
    skillsKeywords: ["Grant writing", "Fundraising", "Volunteer management", "Community outreach", "Program development"],
    achievementPhrases: ["Raised funds totaling", "Increased program participation by", "Managed volunteer team of", "Secured grants worth", "Improved community outcomes"],
    industryTerms: ["Social impact", "Community development", "Program evaluation", "Capacity building", "Social justice"],
    resumeGuidelines: [
      "Emphasize mission alignment and passion for the cause",
      "Highlight measurable social impact and program outcomes",
      "Include fundraising achievements and grant success",
      "Show experience with volunteer management and community engagement",
      "Demonstrate resourcefulness and ability to do more with less"
    ]
  }
};

// Custom hook for easy context access
export function useResumeContext() {
  const resumeData = React.useContext(ResumeDataContext);
  const wordBanks = React.useContext(WordBanksContext);
  const appState = React.useContext(AppStateContext);
  const actions = React.useContext(ResumeActionsContext);

  if (!actions || !appState) {
    throw new Error('useResumeContext must be used within a ResumeProvider');
  }

  return {
    resumeData,
    wordBanks,
    appState,
    // Combined state for convenience
    state: {
      ...appState,
      currentResume: resumeData?.currentResume || '',
      jobDescription: resumeData?.jobDescription || '',
      output: resumeData?.generatedResume || '',
      wordBanks
    },
    ...actions,
  };
}

// Provider component
export function ResumeProvider({ children }: { children: React.ReactNode }) {
  const [resumeData, setResumeData] = useState<ResumeData | null>(null);
  const [wordBanks, setWordBanks] = useState<WordBanks>({
    actionVerbs: JOB_FIELD_PRESETS.technology.actionVerbs,
    skillsKeywords: JOB_FIELD_PRESETS.technology.skillsKeywords,
    achievementPhrases: JOB_FIELD_PRESETS.technology.achievementPhrases,
    industryTerms: JOB_FIELD_PRESETS.technology.industryTerms
  });
  const [appState, setAppState] = useState<AppState>({
    resumeGuidelines: JOB_FIELD_PRESETS.technology.resumeGuidelines,
    profilePicture: null,
    analysisResults: null,
    progressLog: { isVisible: false, phase: 'Ready', messages: [] },
    outputVisible: false,
    selectedJobField: 'technology',
    parsedResumeData: null,
    showResumePreviewModal: false
  });
  const [_isGenerating, setIsGenerating] = useState(false);

  // Word bank actions
  const updateWordBank = useCallback((bankType: keyof WordBanks, items: string[]) => {
    setWordBanks(prev => ({ ...prev, [bankType]: items }));
  }, []);

  const addToWordBank = useCallback((bankType: keyof WordBanks, item: string) => {
    setWordBanks(prev => ({
      ...prev,
      [bankType]: [...prev[bankType], item]
    }));
  }, []);

  const removeFromWordBank = useCallback((bankType: keyof WordBanks, index: number) => {
    setWordBanks(prev => ({
      ...prev,
      [bankType]: prev[bankType].filter((_, i) => i !== index)
    }));
  }, []);

  // Resume guidelines actions
  const setResumeGuidelines = useCallback((guidelines: React.SetStateAction<string[]>) => {
    setAppState(prev => ({
      ...prev,
      resumeGuidelines: typeof guidelines === 'function'
        ? guidelines(prev.resumeGuidelines)
        : guidelines
    }));
  }, []);

  const addGuideline = useCallback((guideline: string) => {
    setAppState(prev => ({
      ...prev,
      resumeGuidelines: [...prev.resumeGuidelines, guideline]
    }));
  }, []);

  const removeGuideline = useCallback((index: number) => {
    setAppState(prev => ({
      ...prev,
      resumeGuidelines: prev.resumeGuidelines.filter((_, i) => i !== index)
    }));
  }, []);

  // Profile picture actions
  const setProfilePicture = useCallback((picture: string | null) => {
    setAppState(prev => ({ ...prev, profilePicture: picture }));
  }, []);

  // Analysis results actions
  const setAnalysisResults = useCallback((results: AnalysisResults | null) => {
    setAppState(prev => ({ ...prev, analysisResults: results }));
  }, []);

  // Progress logging actions
  const logProgress = useCallback((message: string) => {
    setAppState(prev => ({
      ...prev,
      progressLog: {
        ...prev.progressLog,
        messages: [...prev.progressLog.messages, message]
      }
    }));
  }, []);

  const setProgressPhase = useCallback((phase: string) => {
    setAppState(prev => ({
      ...prev,
      progressLog: { ...prev.progressLog, phase }
    }));
  }, []);

  const clearProgressLog = useCallback(() => {
    setAppState(prev => ({
      ...prev,
      progressLog: { isVisible: false, phase: 'Ready', messages: [] }
    }));
  }, []);

  // Show/hide progress modal
  const showProgressModal = useCallback((phase: string) => {
    setAppState(prev => ({
      ...prev,
      progressLog: { isVisible: true, phase, messages: [] }
    }));
  }, []);

  const hideProgressModal = useCallback(() => {
    setAppState(prev => ({
      ...prev,
      progressLog: { ...prev.progressLog, isVisible: false }
    }));
  }, []);

  // Set output
  const setOutput = useCallback((output: string) => {
    setResumeData(prev => ({
      ...prev!,
      generatedResume: output
    }));
  }, []);

  // Output visibility actions
  const setOutputVisible = useCallback((visible: boolean) => {
    setAppState(prev => ({ ...prev, outputVisible: visible }));
  }, []);

  // Job field actions
  const setSelectedJobField = useCallback((field: string) => {
    setAppState(prev => ({ ...prev, selectedJobField: field }));
  }, []);

  const loadJobFieldPreset = useCallback((field: string) => {
    const preset = JOB_FIELD_PRESETS[field];
    if (preset) {
      setWordBanks({
        actionVerbs: preset.actionVerbs,
        skillsKeywords: preset.skillsKeywords,
        achievementPhrases: preset.achievementPhrases,
        industryTerms: preset.industryTerms
      });
      setResumeGuidelines(preset.resumeGuidelines);
      setSelectedJobField(field);
    }
  }, [setResumeGuidelines]);

  // Resume preview modal actions
  const setParsedResumeData = useCallback((data: ParsedResumeData | null) => {
    setAppState(prev => ({ ...prev, parsedResumeData: data }));
  }, []);

  const showResumePreview = useCallback(() => {
    setAppState(prev => ({ ...prev, showResumePreviewModal: true }));
  }, []);

  const hideResumePreview = useCallback(() => {
    setAppState(prev => ({ ...prev, showResumePreviewModal: false }));
  }, []);

  // Helper functions for updating resume data
  const updateCurrentResume = useCallback((resume: string) => {
    setResumeData(prev => prev ? { ...prev, currentResume: resume } : null);
  }, []);

  const updateJobDescription = useCallback((description: string) => {
    setResumeData(prev => prev ? { ...prev, jobDescription: description } : null);
  }, []);

  const generateResume = useCallback(async (onProgress?: (msg: string) => void, onPhase?: (phase: string) => void) => {
    if (!resumeData?.jobDescription || !resumeData?.currentResume) {
      alert('Please provide both job description and current resume');
      return;
    }

    setIsGenerating(true);
    try {
      // Build shared context from word banks + guidelines (matching index.html buildSharedContext)
      let sharedContext = '';
      if (appState.resumeGuidelines.length > 0) {
        sharedContext += `Resume Guidelines:\n${appState.resumeGuidelines.map((g, i) => `${i + 1}. ${g}`).join('\n')}\n\n`;
      }

      // Phase 1/3 - Resume Enhancement
      onPhase?.('Phase 1/3 - Resume Enhancement');
      let generated = await generateTailoredResume(
        resumeData.jobDescription,
        resumeData.currentResume,
        wordBanks,
        onProgress
      );
      onProgress?.('Initial resume generated.');

      // Phase 2/3 - Refinement (if enabled)
      if (resumeData.enableAIRefinement) {
        onPhase?.('Phase 2/3 - AI Refinement (2 passes)');
        onProgress?.('Starting paragraph-by-paragraph refinement...');
        generated = await refineParagraphs(generated, sharedContext, 2, onProgress);
        onProgress?.('Refinement complete.');
      }

      // Phase 3/3 - Complete
      onPhase?.('Phase 3/3 - Finalizing');
      onProgress?.('Setting up resume output...');

      // Update resume data with generated content and persist
      setResumeData(prev => ({
        ...prev!,
        generatedResume: generated
      }));
      try { localStorage.setItem('draftresume_generated_md', generated); } catch { /* quota */ }

      onProgress?.('Resume generation complete!');

    } catch (error) {
      console.error('Error generating resume:', error);
      throw error;
    } finally {
      setIsGenerating(false);
    }
  }, [resumeData, wordBanks, appState.resumeGuidelines]);

  // Memoize actions to prevent unnecessary re-renders
  const actions = useMemo(() => ({
    setResumeData,
    generateResume,
    setWordBanks,
    updateWordBank,
    addToWordBank,
    removeFromWordBank,
    setResumeGuidelines,
    addGuideline,
    removeGuideline,
    setProfilePicture,
    setAnalysisResults,
    logProgress,
    setProgressPhase,
    clearProgressLog,
    showProgressModal,
    hideProgressModal,
    setOutput,
    setOutputVisible,
    setSelectedJobField,
    loadJobFieldPreset,
    setParsedResumeData,
    showResumePreview,
    hideResumePreview,
    updateCurrentResume,
    updateJobDescription
  }), [
    generateResume,
    updateWordBank,
    addToWordBank,
    removeFromWordBank,
    setResumeGuidelines,
    addGuideline,
    removeGuideline,
    setProfilePicture,
    setAnalysisResults,
    logProgress,
    setProgressPhase,
    clearProgressLog,
    showProgressModal,
    hideProgressModal,
    setOutput,
    setOutputVisible,
    setSelectedJobField,
    loadJobFieldPreset,
    setParsedResumeData,
    showResumePreview,
    hideResumePreview,
    updateCurrentResume,
    updateJobDescription
  ]);

  return (
    <ResumeDataContext.Provider value={resumeData}>
      <WordBanksContext.Provider value={wordBanks}>
        <AppStateContext.Provider value={appState}>
          <ResumeActionsContext.Provider value={actions}>
            {children}
          </ResumeActionsContext.Provider>
        </AppStateContext.Provider>
      </WordBanksContext.Provider>
    </ResumeDataContext.Provider>
  );
}

// Alias for backward compatibility
export const useResume = useResumeContext;